import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from 'reactstrap';
import { Bell } from 'react-feather';
import { useSelector } from 'react-redux';
import { notificationService, NotificationPriority } from '../../../services/notificationService';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';
import toast from 'react-hot-toast';
import moment from 'moment';
import { getUser } from '../../../utility/AuthService';

const NotificationCenter = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const user = useSelector(state => state.auth.user) || getUser();
    const languageUse = useSelector(selectLanguageUse || user?.language);

    const loadNotifications = async () => {
        if (!user?.id) {
            console.log('User not loaded yet, skipping notification load');
            return;
        }

        setLoading(true);
        try {
            const userNotifications = await notificationService.getUserNotifications(user.id);
            setNotifications(userNotifications);
            setUnreadCount(userNotifications.filter(n => !n.read).length);
        } catch (error) {
            console.error('Error loading notifications:', error);
            toast.error(languageUse === 'English' ? 'Failed to load notifications' : 'Échec du chargement des notifications');
        }
        setLoading(false);
    };

    useEffect(() => {
        if (user?.id) {
            loadNotifications();
        }
    }, [user?.id]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId, user.id);
            setNotifications(prev =>
                prev.map(notification =>
                    notification.id === notificationId
                        ? { ...notification, read: true }
                        : notification
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            toast.error(languageUse === 'English' ? 'Failed to mark notification as read' : 'Échec du marquage de la notification comme lue');
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await notificationService.deleteNotification(notificationId, user.id);
            setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            toast.error(languageUse === 'English' ? 'Failed to delete notification' : 'Échec de la suppression de la notification');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case NotificationPriority.URGENT:
                return 'danger';
            case NotificationPriority.HIGH:
                return 'warning';
            case NotificationPriority.MEDIUM:
                return 'info';
            case NotificationPriority.LOW:
                return 'secondary';
            default:
                return 'primary';
        }
    };

    const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

    return (
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="notification-dropdown">
            <DropdownToggle tag="div" className="notification-icon">
                <Bell size={20} />
                {unreadCount > 0 && (
                    <Badge color="danger" className="notification-badge">
                        {unreadCount}
                    </Badge>
                )}
            </DropdownToggle>
            <DropdownMenu right className="notification-menu">
                <div className="notification-header">
                    <h6>{languageUse === 'English' ? 'Notifications' : 'Notifications'}</h6>
                </div>
                <div className="notification-body">
                    {loading ? (
                        <div className="d-flex justify-content-center p-3">
                            <Spinner size="sm" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="text-center p-3">
                            {languageUse === 'English' ? 'No notifications' : 'Aucune notification'}
                        </div>
                    ) : (
                        notifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`notification-item ${!notification.read ? 'unread' : ''}`}
                            >
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <Badge color={getPriorityColor(notification.priority)} className="mb-1">
                                            {notification.type}
                                        </Badge>
                                        <p className="mb-1">{notification.message}</p>
                                        <small className="text-muted">
                                            {moment(notification.timestamp).fromNow()}
                                        </small>
                                    </div>
                                    <div className="notification-actions">
                                        {!notification.read && (
                                            <button
                                                className="btn btn-link btn-sm"
                                                onClick={() => handleMarkAsRead(notification.id)}
                                            >
                                                {languageUse === 'English' ? 'Mark as read' : 'Marquer comme lu'}
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-link btn-sm text-danger"
                                            onClick={() => handleDelete(notification.id)}
                                        >
                                            {languageUse === 'English' ? 'Delete' : 'Supprimer'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                {notifications.length > 0 && (
                    <div className="notification-footer">
                        <button
                            className="btn btn-link btn-sm"
                            onClick={() => {
                                notifications.forEach(notification => {
                                    if (!notification.read) {
                                        handleMarkAsRead(notification.id);
                                    }
                                });
                            }}
                        >
                            {languageUse === 'English' ? 'Mark all as read' : 'Tout marquer comme lu'}
                        </button>
                    </div>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default NotificationCenter; 